import axios from "axios";
import { NO_SDC_ERROR } from "./error_codes";
import env from "../tools/Environment";
import BatchMaster from "./BatchMaster";
import checkSdc from "./checkSdc";
/**
 * Default url format function
 * @param {object} meta
 * @returns {string}
 */
const format_url_default = function(meta) {
  let { version, project } = meta;
  if (project) {
    project = "/" + project;
  }
  return `${env.getUrls().api}/api/${version}/jsonrpc${project}`;
};
class BaseApi {
  /**
   * @param  {function} [get_url_func] url format function
   */
  constructor(get_url_func) {
    axios.defaults.headers.common["X-Locale"] = env.getLang(); //current language
    axios.defaults.headers.common["X-ProfileId"] = "";
    this.base_url = env.getUrls().api + "/api/v1/jsonrpc"; //legacy static base url, now we have dynamic url
    this.format_url_func = get_url_func || format_url_default;
    this.api_version = "v1"; //default api version
    this.dev_mode = location.host.split(".").slice(-1)[0] === "test"; //local docker dev_mode
    this.is_batch_support = true; //BatchMaster support
  }
  /**
   *
   * @param {number} request_count
   */
  initBatchMaster(request_count) {
    this.is_batch_support = true;
    BatchMaster.setRequestCount(request_count);
    BatchMaster.setRequestSender(this.rpc.bind(this));
  }

  /**
   *
   * @param {string} service_name
   * @param {ServiceApi} service_instance
   */
  registerService(service_name, service_instance) {
    this[service_name] = service_instance;
    service_instance.setRequestFunc(this.serviceRequest.bind(this, service_instance.prefix));
    service_instance.setRawRequestFunc(this.rawServiceRequest.bind(this));
    if (this.dev_mode) {
      service_instance.enableDevMode();
    }
  }
  /** High-level service api call wrapper
   * @param {string} prefix name of server api method
   * @param {string} method name of server api method
   * @param {object} request_data request method params
   * @param {object} [request_meta] request meta data, like {version:"v1", project"la"}
   * @return {object}
   */
  serviceRequest(prefix, method, request_data, request_meta = null) {
    const request = this.prepareRpcRequest(prefix, method, request_data, request_meta);
    const result = {
      run: this.rpc.bind(this, request, this.getUrl(request_meta)),
      request,
    };
    if (this.is_batch_support) {
      result.batch = BatchMaster.request.bind(BatchMaster, request);
    }

    return result;
  }
  /** High-level service api call wrapper
   * @param {object|array} request json-rpc request object
   * @return {object}
   */
  rawServiceRequest(request) {
    const result = {
      run: this.rpc.bind(this, request),
      request: request,
    };
    if (this.is_batch_support) {
      result.batch = BatchMaster.request.bind(BatchMaster, request);
    }
    return result;
  }

  batch(data, url) {
    return this.rpc(data, url);
  }
  /**
   * Low-level api call wrapper;
   * Send rpc request
   * @param {string} [url]
   * @param {object|array} data json-rpc request data object
   * @return {Promise}
   */

  rpc(data, url = "") {
    const request_url = this.constructRpcUrl(data, url);
    const raw_data_copy = JSON.parse(JSON.stringify(data));
    if (!request_url) {
      throw new Error("Bad rpc call! need valid url");
    }
    data = this.removeMetaData(data);
    data = this.enumerateData(data);
    return axios
      .post(request_url, data)
      .then(response => {
        if (this.isSdcError(response)) {
          return checkSdc().then(() => {
            return this.rpc(raw_data_copy, url);
          });
        } else {
          return response;
        }
      })
      .catch(response => {
        //it's really server issue..
        return {
          data: { error: { message: "SERVER NOT AVAILABLE! PANIC MODE!" }, code: 0 },
          status: response.status,
        };
      });
  }
  enumerateData(request_data) {
    if (Array.isArray(request_data)) {
      return request_data.map((request_data_obj, index) =>
        Object.assign({}, request_data_obj, { id: index + 1 })
      );
    } else {
      return Object.assign({}, request_data, { id: 1 });
    }
  }
  removeMetaData(request_data) {
    if (!this.checkNewMetaFormat(request_data)) {
      return request_data;
    }
    if (Array.isArray(request_data)) {
      return request_data.map(request_data_obj => this.removeMetaDataFromObj(request_data_obj));
    }
    return this.removeMetaDataFromObj(request_data);
  }
  removeMetaDataFromObj(request_data) {
    let copy = Object.assign({}, request_data);
    if (typeof copy.__meta__ !== "undefined") {
      delete copy.__meta__;
    }
    return copy;
  }
  constructRpcUrl(request_data, url = "") {
    if (url) {
      return url;
    }
    let meta = this.checkNewMetaFormat(request_data);
    //legacy api call
    if (!meta) {
      console.warn(
        "Legacy api call, you should provide request meta data, i'll try to request base url.."
      );
      return this.base_url;
    }
    //new api call with meta
    let { version, project } = meta;
    if (Array.isArray(request_data)) {
      const is_valid_request = request_data.every(item => {
        return item.__meta__.version === version && item.__meta__.project === project;
      });
      if (!is_valid_request) {
        throw new Error("Inconsistent request meta data, you can't send this request");
      }
    }
    return this.getUrl(meta);
  }
  checkNewMetaFormat(request_data) {
    if (Array.isArray(request_data)) {
      return request_data[0].__meta__;
    } else {
      return request_data.__meta__;
    }
  }
  getUrl(meta) {
    return this.format_url_func(this.normalizeMeta(meta));
  }
  normalizeMeta(meta = null) {
    let normalized_meta = { version: this.api_version, project: "" };
    if (!meta) {
      return normalized_meta;
    }
    if (meta.hasOwnProperty("version")) {
      normalized_meta.version = meta.version;
    }
    if (meta.hasOwnProperty("project")) {
      normalized_meta.project = meta.project;
    }
    return normalized_meta;
  }

  isSdcError(response) {
    const data = response.data;

    if (Array.isArray(data) && data.length) {
      return data.some(item => this.sdcErrorCondition(item));
    } else {
      return this.sdcErrorCondition(data);
    }
  }

  sdcErrorCondition(object) {
    return object.hasOwnProperty("error") && object.error.code === NO_SDC_ERROR;
  }

  /**
   * Send raw axios request, see https://github.com/axios/axios
   * @param {object} params
   * @return {AxiosPromise}
   */
  request(params) {
    return axios(params);
  }
  /**
   *
   * @param {string} prefix
   * @param {string} method_name
   * @param {object|array} [request_data]
   * @param {object} [request_meta] request_meta request meta data, like {version:"v1", project"la"}
   * @return {object}
   */
  prepareRpcRequest(prefix, method_name, request_data = {}, request_meta = null) {
    return {
      jsonrpc: "2.0",
      method: `${prefix}_${method_name}`,
      params: request_data,
      __meta__: this.normalizeMeta(request_meta),
    };
  }

  /**
   * Set X-ProfileId header with current user id
   * @param {string|number} profile_id
   */
  setProfileIdHeader(profile_id = "") {
    axios.defaults.headers.common["X-ProfileId"] = profile_id;
  }
  /**
   * Set X-Locale header
   * @param {string} lang
   */
  setXLocaleHeader(lang) {
    axios.defaults.headers.common["X-Locale"] = lang;
  }

  /**
   * Helper for response errors detection
   * @param response
   * @returns {boolean}
   */
  isApiResponseError(response) {
    if (response.error) {
      return true;
    }
    if (Array.isArray(response.data)) {
      return response.data.some(item => item.error);
    } else {
      return !!response.data.error;
    }
  }

  /**
   * Return first error from response. If  there is no errors, return nullable value (undefined/null)
   * @param response api response object/array
   * @returns {*}
   */
  getApiResponseError(response) {
    if (response.error) {
      return response.error;
    }
    if (Array.isArray(response.data)) {
      return response.data.find(item => item.error);
    } else {
      return response.data.error;
    }
  }
}
export default BaseApi;
